import React, { useEffect, useState, useRef, useMemo } from "react";
import { User } from "../../../models/user.model";
import { getUsers } from "../../../api/getUsers";
import { ContactPersonType } from "../../../models/bmp.model";

interface InvestmentModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: any) => void;
}

const OWNERSHIP_OPTIONS = [
    { id: ContactPersonType.HDC, label: "Human D.C." },
    { id: ContactPersonType.COMMON, label: "Közös (Csaba és Dávid)" },
    { id: ContactPersonType.CSABA, label: "Csaba" },
    { id: ContactPersonType.DAVID, label: "Dávid" },
];

export const AddInvestmentModal: React.FC<InvestmentModalProps> = ({ isOpen, onClose, onSubmit }) => {
    const [users, setUsers] = useState<User[]>([]);
    const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
    
    // Form States
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [ownershipId, setOwnershipId] = useState<ContactPersonType>(ContactPersonType.HDC);
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [investedEur, setInvestedEur] = useState<string>("");
    const [investedUsd, setInvestedUsd] = useState<string>("");
    
    // Új bevitelre szolgáló mező: Váltási költség (USD)
    const [usdChangeBankFee, setUsdChangeBankFee] = useState<string>("");

    // Deduction States
    const [costDeductionFlag, setCostDeductionFlag] = useState<boolean>(true);
    const [costDeduction, setCostDeduction] = useState<number>(0);

    const dropdownRef = useRef<HTMLDivElement>(null);

    // Initial Load
    useEffect(() => {
        if (isOpen) {
            const loadUsers = async () => {
                try {
                    const data = await getUsers();
                    setUsers(data);
                    setFilteredUsers(data);
                } catch (error) {
                    console.error("Failed to load users", error);
                }
            };
            loadUsers();
        }
    }, [isOpen]);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsUserDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Search filtering
    useEffect(() => {
        const lowerSearch = searchTerm.toLowerCase();
        const filtered = users.filter(user => 
            (user.name?.toLowerCase().includes(lowerSearch) || 
             user.email?.toLowerCase().includes(lowerSearch))
        );
        setFilteredUsers(filtered);
    }, [searchTerm, users]);

    // Live Calculations
    const calculations = useMemo(() => {
        const eurAmount = parseFloat(investedEur) || 0;
        const usdAmount = parseFloat(investedUsd) || 0;
        const exchangeFeeUsd = parseFloat(usdChangeBankFee) || 0;
        
        // Számolt EUR alapú díjak (0.3% és fix 10 EUR)
        const zeroPointThreeBankFee = eurAmount * 0.003;
        const fixTenEuroBankFee = eurAmount > 0 ? 10 : 0; 
        
        // A nettó USD kiszámítása: USD befizetés - 10% (ha aktív)
        // Megjegyzés: Mivel a banki díjak EUR-ban vannak megadva a leírásodban, 
        // de az elszámolás USD alapú, itt a levonás az USD összegből történik.
        const deductionAmount = costDeductionFlag ? usdAmount * 0.1 : 0;
        const finalNet = usdAmount - deductionAmount;
        
        return {
            zeroPointThreeBankFee: Number(zeroPointThreeBankFee.toFixed(2)),
            fixTenEuroBankFee: fixTenEuroBankFee,
            deductionAmount: Number(deductionAmount.toFixed(2)),
            finalNet: finalNet > 0 ? finalNet.toFixed(2) : "0.00"
        };
    }, [investedEur, investedUsd, usdChangeBankFee, costDeductionFlag]);

    // Sync calculated deduction amount to state for backend submission
    useEffect(() => {
        setCostDeduction(calculations.deductionAmount);
    }, [calculations.deductionAmount]);

    const handleUserSelect = (user: User) => {
        setSelectedUser(user);
        setSearchTerm(user.name || user.email || "");
        setIsUserDropdownOpen(false);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedUser) return alert("Kérlek válassz ügyfelet!");

        /*
            zeroPointThreeBankFee: Decimal | null; // Sémában így szerepel
  fixTenEuroBankFee: Decimal | null;     // Sémában így szerepel
  usdChangeBankFee: Decimal | null;      // Sémában így szerepel
        */
        onSubmit({
            user_id: selectedUser.user_id,
            contactPerson: ownershipId,
            date,
            investedEur: Number(investedEur) || 0,
            investedUsd: Number(investedUsd) || 0,
            zeroPointThreeBankFee: calculations.zeroPointThreeBankFee, // EUR-ban küldjük a fix 0.3%-ot
            fixTenEuroBankFee: calculations.fixTenEuroBankFee, // EUR-ban küldjük a fix 10-et
            usdChangeBankFee: Number(usdChangeBankFee) || 0, // USD váltási költség
            costDeductionFlag,
            costDeduction,
            netInvestedUsd: Number(calculations.finalNet),
            pufferedUsd: 0 
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-dark-blue/60 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden border border-cream">
                <div className="flex items-center justify-between p-6 bg-cream/30 border-b border-cream">
                    <h2 className="text-xl font-bold text-dark-brown">Új befizetés rögzítése</h2>
                    <button onClick={onClose} className="text-light-blue hover:text-error transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {/* Ügyfél kiválasztása */}
                    <div className="relative" ref={dropdownRef}>
                        <label className="block text-sm font-semibold text-dark-blue mb-1">Ügyfél kiválasztása</label>
                        <input
                            type="text"
                            placeholder="Keresés..."
                            className="w-full px-4 py-2.5 bg-back border border-light-blue/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-brown/50"
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setIsUserDropdownOpen(true);
                                setSelectedUser(null);
                            }}
                            onFocus={() => setIsUserDropdownOpen(true)}
                        />
                        {isUserDropdownOpen && filteredUsers.length > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-cream rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                {filteredUsers.map((user) => (
                                    <button
                                        key={user.user_id}
                                        type="button"
                                        className="w-full text-left px-4 py-3 hover:bg-ultra-light/50 border-b last:border-0"
                                        onClick={() => handleUserSelect(user)}
                                    >
                                        <span className="font-semibold block">{user.name}</span>
                                        <span className="text-xs text-light-blue">{user.email}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-dark-blue mb-1">Felelős</label>
                            <select
                                value={ownershipId}
                                onChange={(e) => setOwnershipId(e.target.value as ContactPersonType)}
                                className="w-full px-4 py-2.5 bg-back border border-light-blue/20 rounded-lg"
                            >
                                {OWNERSHIP_OPTIONS.map(opt => (
                                    <option key={opt.id} value={opt.id}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-dark-blue mb-1">Dátum</label>
                            <input
                                type="date"
                                required
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                className="w-full px-4 py-2.5 bg-back border border-light-blue/20 rounded-lg"
                            />
                        </div>
                    </div>

                    <hr className="border-cream my-2" />

                    {/* Befizetés összegek */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-dark-blue mb-1">Befizetett (EUR)</label>
                            <input
                                type="number"
                                step="any"
                                value={investedEur}
                                onChange={(e) => setInvestedEur(e.target.value)}
                                className="w-full px-4 py-2.5 bg-back border border-light-blue/20 rounded-lg font-mono"
                                placeholder="0.00"
                            />
                            {/* Automatikus EUR levonások megjelenítése */}
                            <div className="mt-2 space-y-1">
                                <div className="flex justify-between text-[11px] text-light-blue px-1">
                                    <span>Bank (0.3%):</span>
                                    <span className="font-bold">-{calculations.zeroPointThreeBankFee} EUR</span>
                                </div>
                                <div className="flex justify-between text-[11px] text-light-blue px-1">
                                    <span>Tranzakciós díj:</span>
                                    <span className="font-bold">-{calculations.fixTenEuroBankFee} EUR</span>
                                </div>
                            </div>
                        </div>
                         <div>
                            <label className="block text-sm font-semibold text-dark-blue mb-1">Befizetett EUR (USD-ben)</label>
                            <input
                                type="number"
                                step="any"
                                value={investedUsd}
                                onChange={(e) => setInvestedUsd(e.target.value)}
                                className="w-full px-4 py-2.5 bg-back border border-light-blue/20 rounded-lg font-mono"
                                placeholder="0.00"
                            />
                        </div>
                    </div>

                    {/* Manuális USD Költség és Checkbox */}
                    <div className="grid grid-cols-2 gap-4 items-end bg-ultra-light/30 p-3 rounded-lg border border-cream/50">
                        <div>
                            <label className="block text-[10px] font-bold text-light-blue mb-1 uppercase">Váltási költség (USD)</label>
                            <input
                                type="number"
                                step="any"
                                value={usdChangeBankFee}
                                onChange={(e) => setUsdChangeBankFee(e.target.value)}
                                className="w-full px-3 py-2 bg-white border border-light-blue/20 rounded-lg text-sm font-mono"
                                placeholder="0.00"
                            />
                        </div>
                        <div className="flex flex-col space-y-1">
                            <div className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    id="costDeduction"
                                    checked={costDeductionFlag}
                                    onChange={(e) => setCostDeductionFlag(e.target.checked)}
                                    className="w-4 h-4 accent-brown rounded cursor-pointer"
                                />
                                <label htmlFor="costDeduction" className="text-[10px] font-bold text-dark-blue leading-tight uppercase cursor-pointer">
                                    10% levonás
                                </label>
                            </div>
                            <div className="text-[11px] font-mono text-error font-bold pl-6">
                                -${calculations.deductionAmount}
                            </div>
                        </div>
                    </div>

                    {/* Nettó Eredmény */}
                    <div className="bg-dark-blue/5 p-4 rounded-xl border border-dark-blue/10 flex justify-between items-end">
                        <div>
                            <label className="block text-sm font-bold text-dark-blue mb-1">Nettó Befektetett (USD)</label>
                            <div className="text-3xl font-mono font-bold text-brown">
                                $ {calculations.finalNet}
                            </div>
                        </div>
                        <div className="text-right pb-1">
                            <p className="text-[10px] text-light-blue italic">
                                USD levonások összesen: -${(Number(usdChangeBankFee) + costDeduction).toFixed(2)}
                            </p>
                        </div>
                    </div>

                    <div className="flex gap-3 pt-4 border-t border-cream">
                        <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 rounded-lg border border-light-blue/30 text-light-blue font-semibold hover:bg-back transition-colors">Mégse</button>
                        <button type="submit" className="flex-1 px-4 py-2.5 rounded-lg bg-brown text-white font-semibold hover:bg-dark-brown transition-colors">Mentés</button>
                    </div>
                </form>
            </div>
        </div>
    );
};